import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { ImageRecord } from "@rb/common";
import { fetchImages, imageFileUrl } from "../../lib/api";
import { useZoomPan } from "./useZoomPan";
import styles from "./CamPage.module.scss";

const REFRESH_MS = 60_000;

interface Layer {
  image: ImageRecord;
  /** True once the browser has the whole file; only then does the layer fade in. */
  loaded: boolean;
}

/**
 * The most recent photo, full page, letterboxed rather than stretched. Polls for a
 * newer one every minute. A new photo is added underneath as a hidden layer, and only
 * once its `load` event fires does it fade in over the old one, so there is never a
 * half-decoded or blank frame. The user can zoom and pan (wheel, trackpad or touch
 * pinch, drag, double-tap to reset); the view is kept across swaps because the
 * transform sits on the container, not the photo. `?device=<deviceId>` narrows it to
 * one phone.
 */
export function CamPage() {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get("device");
  const [layers, setLayers] = useState<Layer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const viewportRef = useRef<HTMLElement | null>(null);
  const zoom = useZoomPan({ viewportRef });

  const load = useCallback(async () => {
    try {
      const page = await fetchImages({ deviceId, limit: 1 });
      const latest = page.images[0] ?? null;
      setError(null);
      setCheckedAt(new Date());
      setLayers((current) => {
        if (latest === null) return [];
        if (current.some((layer) => layer.image.id === latest.id)) return current;
        // Keep the visible layer(s); drop any earlier pending one that never finished.
        return [...current.filter((layer) => layer.loaded), { image: latest, loaded: false }];
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [deviceId]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const markLoaded = (id: string) =>
    setLayers((current) =>
      current.map((layer) => (layer.image.id === id ? { ...layer, loaded: true } : layer)),
    );

  // Once the newest layer has fully faded in, the ones beneath it are invisible; remove them.
  const dropBeneath = (id: string) =>
    setLayers((current) => {
      const index = current.findIndex((layer) => layer.image.id === id);
      return index <= 0 ? current : current.slice(index);
    });

  const shown = [...layers].reverse().find((layer) => layer.loaded) ?? null;

  return (
    <main
      ref={viewportRef}
      className={classNames(styles["page"], zoom.zoomed && styles["zoomed"])}
      {...zoom.handlers}>
      {layers.length > 0 ? (
        <>
          <div
            className={styles["stage"]}
            style={{ transform: zoom.transform }}
            data-testid="stage">
            {layers.map((layer) => (
              <img
                key={layer.image.id}
                className={classNames(styles["photo"], layer.loaded && styles["loaded"])}
                src={imageFileUrl(layer.image)}
                alt={`Captured ${formatDate(layer.image.capturedDate)}`}
                draggable={false}
                onLoad={() => markLoaded(layer.image.id)}
                onTransitionEnd={() => dropBeneath(layer.image.id)}
              />
            ))}
          </div>
          {shown !== null && (
            <div className={styles["caption"]}>
              <span>{formatDate(shown.image.capturedDate)}</span>
              {error !== null && <span className={styles["stale"]}>offline · {error}</span>}
            </div>
          )}
        </>
      ) : (
        <p className={styles["empty"]}>{emptyMessage(error, checkedAt)}</p>
      )}
    </main>
  );
}

function emptyMessage(error: string | null, checkedAt: Date | null): string {
  if (error !== null) return `Cannot reach the API (${error})`;
  if (checkedAt === null) return "Loading…";
  return "No photos yet.";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
